import React, { useCallback, useEffect, useState } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { abbrevHash } from "./sha256";

const API = process.env.REACT_APP_BACKEND_URL;

const DATE_LOCALE = { en: "en-SG", zh: "zh-Hans-SG" };

function fmtDate(locale, isoDate) {
  if (!isoDate) return "";
  try {
    const d = typeof isoDate === "string" && isoDate.length === 10
      ? new Date(`${isoDate}T00:00:00Z`)
      : new Date(isoDate);
    return new Intl.DateTimeFormat(DATE_LOCALE[locale] || "en-SG", {
      day: "2-digit", month: "short", year: "numeric",
    }).format(d);
  } catch (_err) {
    return String(isoDate);
  }
}

function StatusBadge({ doc, t }) {
  if (doc.status === "superseded") {
    return (
      <Badge variant="neutral" onDark>
        {t("list.card.statusSuperseded")}
      </Badge>
    );
  }
  if (doc.btc_status === "confirmed") {
    return (
      <Badge variant="success" onDark>
        {t("list.card.statusConfirmed")}
      </Badge>
    );
  }
  return (
    <Badge variant="warning" onDark>
      {t("list.card.statusPending")}
    </Badge>
  );
}

function HashRow({ hash, t }) {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(() => {
    if (!hash || !navigator?.clipboard?.writeText) return;
    navigator.clipboard.writeText(hash).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    });
  }, [hash]);

  return (
    <div className="mt-5 flex items-center gap-3">
      <span className="text-[11px] font-semibold uppercase tracking-caps text-ink-tertiary">
        {t("list.card.fingerprintLabel")}
      </span>
      <code className="truncate font-mono text-[13px] text-ink-primary">
        {abbrevHash(hash)}
      </code>
      <button
        type="button"
        onClick={onCopy}
        aria-label={t("list.card.copyHash")}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-hairline text-ink-secondary transition-colors duration-fast ease-out hover:border-active-border hover:text-frac-violet focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-frac-violet focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base"
        data-testid={`copy-hash-${hash.slice(0, 8)}`}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
        ) : (
          <Copy className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
        )}
      </button>
      {copied && (
        <span className="text-xs text-ink-tertiary" role="status">
          {t("list.card.hashCopied")}
        </span>
      )}
    </div>
  );
}

function DocCard({ doc }) {
  const { t } = useT("verifiedDocuments");
  const locale = useLocale();
  const metaDate = fmtDate(locale, doc.publication_date);
  const sealedDate = fmtDate(locale, doc.sealed_at);
  const confirmedDate = fmtDate(locale, doc.bitcoin_block_time_utc);
  const supersededDate = fmtDate(locale, doc.superseded_at);

  return (
    <Card variant="glass" className="relative" data-testid={`doc-card-${doc.slug}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold tracking-tight">
            {doc.title}
          </h3>
          <p className="mt-2 text-sm text-ink-secondary">
            {t("list.card.metaByFrac")} · {metaDate} ·{" "}
            {t("list.card.metaVersion", { version: doc.version })}
          </p>
        </div>
        <StatusBadge doc={doc} t={t} />
      </div>

      <HashRow hash={doc.sha256 || ""} t={t} />

      <p className="mt-5 text-sm leading-relaxed text-ink-secondary">
        {doc.status === "superseded"
          ? t("list.card.supersededNote", {
              date: supersededDate,
              currentVersion: doc.superseded_by_slug?.match(/v(\d+)$/)?.[1] || "—",
            })
          : doc.btc_status === "confirmed"
            ? t("list.card.confirmedNote", {
                sealedDate,
                confirmedDate,
                height: doc.bitcoin_block_height?.toLocaleString?.() || "—",
              })
            : t("list.card.pendingNote", { sealedDate })}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        {doc.current_download_url && (
          <Button
            as="a"
            href={doc.current_download_url}
            target="_blank"
            rel="noopener noreferrer"
            variant="primary-on-dark"
            size="sm"
            data-testid={`open-doc-${doc.slug}`}
          >
            {t("list.card.openDocument")}
          </Button>
        )}
        {doc.bitcoin_mempool_url && (
          <a
            href={doc.bitcoin_mempool_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-semibold text-frac-violet hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-frac-violet focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base"
            data-testid={`mempool-link-${doc.slug}`}
          >
            {t("list.card.mempoolLink")}
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
          </a>
        )}
        {doc.ots_upgraded_download_url && (
          <a
            href={`${API}${doc.ots_upgraded_download_url}`}
            className="text-sm text-ink-secondary hover:text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-frac-violet focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base"
          >
            {t("list.card.downloadUpgradedOts")}
          </a>
        )}
        {!doc.ots_upgraded_download_url && doc.ots_initial_download_url && (
          <a
            href={`${API}${doc.ots_initial_download_url}`}
            className="text-sm text-ink-secondary hover:text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-frac-violet focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base"
          >
            {t("list.card.downloadInitialOts")}
          </a>
        )}
      </div>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid gap-5" data-testid="doc-list-loading">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="h-40 animate-pulse rounded-[16px] border border-hairline bg-surface-raised/50"
        />
      ))}
    </div>
  );
}

function EmptyState() {
  const { t } = useT("verifiedDocuments");
  return (
    <div
      className="rounded-[16px] border border-hairline bg-[rgba(21,10,36,0.4)] p-10 text-center"
      data-testid="doc-list-empty"
    >
      <p className="text-lg font-semibold text-ink-primary">
        {t("list.empty.title")}
      </p>
      <p className="mt-2 text-sm text-ink-secondary">
        {t("list.empty.caption")}
      </p>
    </div>
  );
}

function ErrorState({ onRetry }) {
  const { t } = useT("verifiedDocuments");
  return (
    <div
      className="rounded-[16px] border border-hairline bg-[rgba(21,10,36,0.4)] p-10 text-center"
      data-testid="doc-list-error"
    >
      <p className="text-base text-ink-primary">{t("list.error.title")}</p>
      <div className="mt-4">
        <Button
          variant="secondary-on-dark"
          size="sm"
          onClick={onRetry}
          data-testid="doc-list-retry"
        >
          {t("list.error.retry")}
        </Button>
      </div>
    </div>
  );
}

/**
 * Fetches the published documents list and renders cards.
 * Exposes the fetched documents via the `onLoad(docs)` callback so the
 * verifier can match against the same list without a second request.
 */
export function DocumentList({ onLoad }) {
  const { t } = useT("verifiedDocuments");
  const [state, setState] = useState({
    phase: "loading", // 'loading' | 'ready' | 'empty' | 'error'
    docs: [],
  });

  const load = useCallback(() => {
    setState({ phase: "loading", docs: [] });
    const url = `${API}/api/documents`;
    fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((docs) => {
        const list = Array.isArray(docs) ? docs : [];
        setState({ phase: list.length === 0 ? "empty" : "ready", docs: list });
        onLoad?.(list);
      })
      .catch(() => {
        setState({ phase: "error", docs: [] });
        onLoad?.([]);
      });
  }, [onLoad]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section
      id="published-documents"
      className="border-t border-hairline py-16 md:py-20"
      data-testid="verified-list"
    >
      <div className="mx-auto max-w-6xl px-6">
        <h2 className="font-sans text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
          {t("list.sectionTitle")}
        </h2>
        <div className="mt-10">
          {state.phase === "loading" && <LoadingSkeleton />}
          {state.phase === "empty" && <EmptyState />}
          {state.phase === "error" && <ErrorState onRetry={load} />}
          {state.phase === "ready" && (
            <Stagger gap={70} className="grid gap-5">
              {state.docs.map((d) => (
                <StaggerItem key={d.slug}>
                  <DocCard doc={d} />
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </div>
    </section>
  );
}

export default DocumentList;
